import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, Timestamp, deleteDoc } from "firebase/firestore";
import { deleteUser } from "firebase/auth";

export default function ConsentStatus() {
  const [consentDate, setConsentDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [warning, setWarning] = useState("");

  useEffect(() => {
    const fetchConsent = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();
          const date = data.consentDate?.toDate();

          if (date) {
            setConsentDate(date);

            const exp = new Date(date);
            exp.setFullYear(exp.getFullYear() + 1); // 12 months later
            setExpiryDate(exp);

            const daysLeft = Math.floor((exp - new Date()) / (1000 * 60 * 60 * 24));

            if (daysLeft <= 30 && daysLeft >= 0) {
              setWarning(`⚠️ Your consent will expire in ${daysLeft} day(s). Please renew soon.`);
            } else if (daysLeft < 0) {
              setWarning("❌ Your consent has expired. Please renew now.");
            }
          }
        }
      } catch (err) {
        console.error("Error fetching consent:", err);
      }

      setLoading(false);
    };

    fetchConsent();
  }, []);

  // ✅ Renew Consent
  const handleRenew = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        { consentDate: Timestamp.now() },
        { merge: true }
      );

      const newDate = new Date();
      setConsentDate(newDate);

      const newExpiry = new Date(newDate);
      newExpiry.setFullYear(newExpiry.getFullYear() + 1);
      setExpiryDate(newExpiry);

      setWarning(""); // clear warning after renewal
      setMessage("✅ Consent renewed successfully!");
      setTimeout(() => setMessage(""), 4000);
    } catch (err) {
      console.error("Error renewing consent:", err);
      setMessage("❌ Failed to renew. Please try again.");
    }
  };

  // ✅ Delete My Data
  const handleDeleteData = async () => {
    const confirmDelete = window.confirm(
      "⚠️ This will permanently delete your account and all data. Are you sure?"
    );
    if (!confirmDelete) return;

    try {
      const user = auth.currentUser;
      if (!user) return;

      const userRef = doc(db, "users", user.uid);
      await deleteDoc(userRef);

      await deleteUser(user);

      window.location.href = "/login";
    } catch (err) {
      console.error("Error deleting user data:", err);
      setMessage("❌ Failed to delete account. Please try again.");
    }
  };

  if (loading) return <p>Loading consent info...</p>;

  return (
    <div className="consent-status">
      <h3>📝 Consent Status</h3>

      {consentDate ? (
        <>
          <p>Last consented: {consentDate.toDateString()}</p>
          <p>Expires on: {expiryDate?.toDateString()}</p>
        </>
      ) : (
        <p>No consent record found. Please provide consent.</p>
      )}

      {/* ✅ Warning messages */}
      {warning && (
        <p
          className={
            warning.includes("expire in")
              ? "consent-warning expiring"
              : "consent-warning expired"
          }
        >
          {warning}
        </p>
      )}

      {/* ✅ Success / error messages */}
      {message && <p className="consent-message">{message}</p>}

      <button onClick={handleRenew} className="consent-renew-btn">
        Renew Consent Now
      </button>

      <hr style={{ margin: "15px 0" }} />

      <button onClick={handleDeleteData} className="consent-delete-btn">
        Delete My Data
      </button>
    </div>
  );
}
